import type { IAgentTool, AgentToolResult } from '../IAgentTool';
import type { RdmDocumentType } from '../../../../domains/rdm/models';
import { ApplyRdmSceneCommand, RdmSceneApplyError } from '../../../commands/ApplyRdmSceneCommand';
import { getNodeMutationManager } from '../../../commands/mutations/NodeMutationManager';

const DIAGRAM_TYPE_MAP: Record<string, RdmDocumentType> = {
  orgchart: 'orgchart',
  'org-chart': 'orgchart',
  genogram: 'genogram',
  process: 'process',
  flowchart: 'process',
  bpmn: 'process',
  network: 'network',
  table: 'table',
  generic: 'generic',
};

/**
 * RDM Apply Tool
 *
 * The structural authoring tool for diagram-as-RDM domains (process,
 * orgchart, genogram, table, network). The agent generates a COMPLETE RDM
 * document — which LLMs produce well — and this tool parses, lays it out, and
 * writes it to the live canvas through an undoable command. One apply = one
 * undo step.
 *
 * Whole-document apply is deliberate: it's the one robust path for both new
 * diagrams (the document replaces an empty canvas) and edits (read the current
 * diagram with rdm_context mode="full", change it, re-apply the whole thing).
 * It sidesteps fragile fragment-splicing — connectors and swimlane banding
 * stay globally consistent because the whole diagram is re-laid-out together.
 *
 * Materialization reuses the same stack as the Toolbar RDM import:
 *   parseAndValidate → toLayoutedRuntimeModel (ELK for process/bpmn)
 *   → ApplyRdmSceneCommand → replaceSceneInWorkspace.
 */
export const rdmApplyTool: IAgentTool = {
  name: 'rdm_apply',
  description:
    'Apply a COMPLETE RDM document to create a diagram. Call rdm_schema for domain syntax and rdm_validate first. ' +
    'For Process diagrams, use this ONLY on an empty canvas; edits use propose_process_changes. ' +
    'Whole-process rebuilds are not supported by this tool. ' +
    'For other domains, read rdm_context mode="full" before editing and check its truncation metadata; partial context cannot justify replacing omitted content. ' +
    'Layout is automatic — never include coordinates.',
  parameters: {
    type: 'object',
    properties: {
      rdm: {
        type: 'string',
        description:
          'The COMPLETE RDM document to render as the diagram (frontmatter + the domain block, ' +
          'e.g. a full `process Name { ... }`). Process creation requires an empty canvas; routine edits use propose_process_changes.',
      },
    },
    required: ['rdm'],
  },
  supportedDiagramTypes: [],
  tier: 'structural',
  category: 'edit',

  async execute(params, context): Promise<AgentToolResult> {
    // `rdm` is the canonical param; accept `fragment` as a legacy alias.
    const raw = String(params.rdm ?? params.fragment ?? '').trim();
    if (!raw) {
      return { success: false, summary: 'Missing rdm parameter — pass the complete RDM document.' };
    }

    if (!context.canWrite) {
      return { success: false, summary: 'You do not have edit access to this diagram.' };
    }

    const domain = DIAGRAM_TYPE_MAP[context.diagramType] || 'generic';
    if (domain === 'process' && (context.diagramData.nodes.length || context.diagramData.edges.length)) {
      return {
        success: false,
        summary: 'Process RDM creation requires an empty canvas. Use propose_process_changes for edits; whole-process rebuilds are not supported here.',
        data: { code: 'PROCESS_EDIT_REQUIRES_CHANGE_SET', suggestedTool: 'propose_process_changes' },
      };
    }
    const emptySceneTarget = domain === 'process' ? { doc: getNodeMutationManager().getDoc() ?? null, bindingVersion: getNodeMutationManager().getDocumentBindingVersion?.() ?? 0 } : undefined;
    const documentText = ensureFullDocument(raw, domain);
    const { rdmService } = await import('../../../../domains/rdm/runtime');

    const parsed = rdmService.parseAndValidate(documentText);
    if (!parsed.success || !parsed.document) {
      const errorMessages = parsed.errors.map((e) => e.message).join('; ');
      return {
        success: false,
        summary: `Invalid RDM: ${errorMessages || 'could not parse the diagram.'}`,
        data: { errors: parsed.errors },
      };
    }

    if (domain === 'process' && !['process', 'bpmn'].includes(parsed.document.meta.type)) {
      return { success: false, summary: 'Use a Process RDM document for this process diagram.' };
    }

    let nodes;
    let edges;
    try {
      // 'redistribute' — re-pick edge anchors from post-layout geometry
      // (matches the production materializers). The 'preserve' default leaves
      // connectors entering on the wrong side after ELK re-lays-out nodes.
      const layouted = await rdmService.toLayoutedRuntimeModel(parsed.document, {
        anchorMode: 'redistribute',
      });
      nodes = layouted.nodes;
      edges = layouted.edges;
    } catch (error) {
      return {
        success: false,
        summary: `Failed to lay out the diagram: ${error instanceof Error ? error.message : String(error)}`,
      };
    }

    if (nodes.length === 0) {
      return { success: false, summary: 'The RDM produced no nodes — nothing to apply.' };
    }

    const command = new ApplyRdmSceneCommand({ nodes, edges, emptySceneTarget });
    const appliedSummary = `Applied the diagram: ${nodes.length} node(s), ${edges.length} edge(s).`;
    if (emptySceneTarget) {
      return {
        success: true,
        summary: `Prepared the process: ${nodes.length} node(s), ${edges.length} edge(s).`,
        execution: {
          kind: 'command-sequence',
          run: async (executor) => {
            try {
              await executor.execute(command);
              return {
                summary: appliedSummary,
                createdResourceIds: nodes.map((node) => node.id),
                data: { nodeCount: nodes.length, edgeCount: edges.length },
              };
            } catch (error) {
              if (!(error instanceof RdmSceneApplyError)) throw error;
              return { success: false, summary: error.message, data: { code: error.code, needsInput: true } };
            }
          },
        },
      };
    }

    return {
      success: true,
      summary: appliedSummary,
      createdResourceIds: nodes.map((n) => n.id),
      data: { nodeCount: nodes.length, edgeCount: edges.length },
      execution: { kind: 'command', command },
    };
  },
};

/**
 * Wrap bare RDM declarations in frontmatter so the parser/hydrator know the
 * domain. If the text already carries frontmatter (`---` / `@type`), it's a
 * full document and we leave it untouched.
 */
function ensureFullDocument(text: string, domain: RdmDocumentType): string {
  const trimmed = text.trimStart();
  if (trimmed.startsWith('---') || trimmed.startsWith('@type') || trimmed.startsWith('@title')) {
    return text;
  }
  return `---\ntype: ${domain}\ntitle: "Untitled"\n---\n\n${text}`;
}
